import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const isLikelyEmail = (value) => {
    if (typeof value !== 'string') return false;
    return value.includes('@');
};

const createKnownAsResolver = (organizationTeam) => {
    const teamData = organizationTeam?.data;
    if (!teamData) return () => null;

    const emailMap = new Map();
    const idMap = new Map();

    const addEntry = (candidate, knownAs, map) => {
        if (!candidate || !knownAs) return;
        const key = String(candidate).trim().toLowerCase();
        if (!key) return;
        if (!map.has(key)) {
            map.set(key, String(knownAs).trim());
        }
    };

    const collectPeople = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((person) => {
            if (!person) return;
            const knownAs =
                (typeof person.KnownAs === 'string' && person.KnownAs.trim()) ||
                (typeof person.known_as === 'string' && person.known_as.trim()) ||
                (typeof person.knownAs === 'string' && person.knownAs.trim()) ||
                (typeof person.ScreenName === 'string' && person.ScreenName.trim()) ||
                null;
            if (!knownAs) return;

            [
                person.EmailAddress,
                person.Email,
                person.email,
                person.LoginID,
                person.login_id,
                person.UserName,
                person.Username,
                person.username,
                person.PrimaryEmail,
                person.primary_email,
            ].forEach((value) => addEntry(value, knownAs, emailMap));

            [
                person.id,
                person.People_id,
                person.PeopleID,
                person.people_id,
                person.team_people_id,
                person.user_id,
                person.UserID,
                person.TeamPeopleID,
            ].forEach((value) => addEntry(value, knownAs, idMap));
        });
    };

    collectPeople(teamData?.members);
    collectPeople(teamData?.coaches);
    collectPeople(teamData?.viewers);
    collectPeople(teamData?.leaders);
    collectPeople(teamData?.participants);

    return (value) => {
        if (value === undefined || value === null) return null;
        const key = String(value).trim().toLowerCase();
        if (!key) return null;
        return emailMap.get(key) || idMap.get(key) || null;
    };
};

const deriveScreenName = (event, resolveKnownAs) => {
    const explicitKnownAs =
        (typeof event?.KnownAs === 'string' && event.KnownAs.trim()) ||
        (typeof event?.knownAs === 'string' && event.knownAs.trim()) ||
        (typeof event?.known_as === 'string' && event.known_as.trim());
    if (explicitKnownAs) {
        return explicitKnownAs.trim();
    }

    const directKeys = [
        'screen_name',
        'ScreenName',
        'screenName',
        'screenname',
        'user_screen_name',
        'userScreenName',
        'assignee_screen_name',
        'assigneeScreenName',
        'member_screen_name',
        'memberScreenName',
        'owner_screen_name',
        'ownerScreenName',
        'createdByScreenName',
        'created_by_screen_name',
        'created_by_screenname',
        'createdByKnownAs',
        'created_to_known_as',
        'assigned_to_known_as',
        'assignedToKnownAs',
        'AssignedToKnownAs',
        'ownerKnownAs',
        'OwnerKnownAs',
        'owner_known_as',
        'Owner_known_as',
        'ownerKnownas',
        'created_by_known_as',
        'known_as',
        'knownAs',
        'KnownAs',
        'display_name',
        'displayName',
        'createdBy',
    ];

    const nestedCandidates = [
        event?.user?.screen_name,
        event?.user?.ScreenName,
        event?.user?.screenName,
        event?.user?.known_as,
        event?.user?.knownAs,
        event?.user?.KnownAs,
        event?.user?.display_name,
        event?.user?.displayName,
        event?.owner?.screen_name,
        event?.owner?.ScreenName,
        event?.owner?.screenName,
        event?.owner?.known_as,
        event?.owner?.knownAs,
        event?.owner?.KnownAs,
        event?.owner?.display_name,
        event?.owner?.displayName,
        event?.assignee?.screen_name,
        event?.assignee?.ScreenName,
        event?.assignee?.screenName,
        event?.assignee?.known_as,
        event?.assignee?.knownAs,
        event?.assignee?.KnownAs,
        event?.assignee?.display_name,
        event?.assignee?.displayName,
        event?.member?.screen_name,
        event?.member?.ScreenName,
        event?.member?.screenName,
        event?.member?.known_as,
        event?.member?.knownAs,
        event?.member?.KnownAs,
        event?.member?.display_name,
        event?.member?.displayName,
        event?.createdBy?.screen_name,
        event?.createdBy?.ScreenName,
        event?.createdBy?.screenName,
        event?.createdBy?.known_as,
        event?.createdBy?.knownAs,
        event?.createdBy?.KnownAs,
        event?.createdBy?.display_name,
        event?.createdBy?.displayName,
        event?.createdBy?.name,
    ];

    const nonEmail = [];
    const emails = [];

    const consider = (candidate) => {
        if (!candidate) return;
        const value = typeof candidate === 'string' ? candidate.trim() : candidate;
        if (!value) return;
        if (isLikelyEmail(value)) {
            emails.push(candidate);
        } else {
            nonEmail.push(value);
        }
    };

    directKeys.forEach((key) => consider(event?.[key]));
    nestedCandidates.forEach(consider);

    if (nonEmail.length > 0) return nonEmail[0];

    const emailCandidates = [
        event?.createdBy,
        event?.email,
        event?.Email,
        event?.EmailAddress,
        event?.owner_email,
        event?.ownerEmail,
        event?.assigned_to_email,
        event?.assignedToEmail,
        event?.assignee_email,
        event?.assigneeEmail,
        event?.user?.EmailAddress,
        event?.user?.email,
        event?.owner?.EmailAddress,
        event?.owner?.email,
        event?.assignee?.EmailAddress,
        event?.assignee?.email,
        event?.member?.EmailAddress,
        event?.member?.email,
    ];

    if (typeof resolveKnownAs === 'function') {
        for (const email of emailCandidates) {
            const match = resolveKnownAs(email);
            if (match) return match;
        }
    }

    const idCandidates = [
        event?.team_people_id,
        event?.TeamPeopleID,
        event?.people_id,
        event?.People_id,
        event?.PeopleID,
        event?.user_id,
        event?.UserID,
        event?.owner_id,
        event?.OwnerID,
        event?.assignee_id,
        event?.AssigneeID,
        event?.assigned_to_id,
        event?.AssignedToID,
    ];

    if (typeof resolveKnownAs === 'function') {
        for (const id of idCandidates) {
            const match = resolveKnownAs(id);
            if (match) return match;
        }
    }

    if (emails.length > 0) return emails[0];

    return null;
};

const buildDisplayTitle = (event, screenName) => {
    const baseTitle = event?.title || event?.name || event?.item_name || '';

    if (!screenName) return baseTitle;

    // Avoid appending duplicate screen names if already present
    const normalizedBase = baseTitle.toLowerCase();
    const normalizedScreen = typeof screenName === 'string' ? screenName.toLowerCase() : '';

    if (normalizedScreen && normalizedBase.includes(normalizedScreen)) {
        return baseTitle;
    }

    return [baseTitle, screenName].filter(Boolean).join(' - ');
};

export const useTeamEvents = (teamId) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const organizationTeam = useSelector((state) => state.organization_team);
    const resolveKnownAs = useMemo(() => createKnownAsResolver(organizationTeam), [organizationTeam]);

    useEffect(() => {
        if (!teamId) return;

        axios.get(`${process.env.REACT_APP_ZYNITY_API_URL}/api/team-events?team_id=${teamId}`)
            .then(response => {
                const parsed = response.data.map(event => {
                    const screenName = deriveScreenName(event, resolveKnownAs);
                    const displayTitle = buildDisplayTitle(event, screenName);

                    return {
                        ...event,
                        start: new Date(event.start),
                        end: new Date(event.end),
                        displayTitle,
                        displayScreenName: screenName,
                    };
                });
                setEvents(parsed);
                setLoading(false);
            })

            .catch(error => {
                console.error('Failed to load events:', error);
                setLoading(false);
            });
        console.log('[useTeamEvents] Fetching for team_id:', teamId);

    }, [teamId, resolveKnownAs]);

    return { events, loading };
};
